import React from 'react';
import {
  LayoutDashboard,
  FileText,
  CheckSquare,
  FileCheck,
  Award,
  Send,
  Building2,
  FileSpreadsheet,
  ShoppingBag,
  PackageCheck,
  Receipt,
  CreditCard,
  BarChart3,
  Bot,
  ShieldCheck,
  Activity,
  Layers,
  Users,
  Sliders,
  Database,
  SlidersHorizontal,
  Truck,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { Permission, Role, User } from '@/types/domain';
import { ROLE_PORTALS_MAP } from '@/config/permissions';

export type PortalContext = 'MAIN' | 'INTERNAL_PROCUREMENT' | 'SAP_SIMULATOR' | 'ADMIN';

export interface NavigationItemConfig {
  id: string;
  label: string;
  path: string;
  icon: React.ElementType;
  group: string;
  portal: PortalContext;
  requiredPermissions?: Permission[];
  requiredRoles?: Role[];
  badge?: string;
  featureFlag?: string;
  isSap?: boolean;
}

export const PORTAL_CONFIG: Record<PortalContext, { name: string; description: string; requiredPermission?: Permission }> = {
  MAIN: {
    name: 'Main Portal',
    description: 'Executive overview, core procurement lifecycle & intelligence',
  },
  INTERNAL_PROCUREMENT: {
    name: 'Internal Procurement',
    description: 'Employee requisitions, approval queue, and budget tracking',
    requiredPermission: 'REQUEST_VIEW',
  },
  SAP_SIMULATOR: {
    name: 'SAP Simulator',
    description: 'SAP S/4HANA OData & BAPI enterprise integration testing environment',
    requiredPermission: 'PR_VIEW',
  },
  ADMIN: {
    name: 'Admin Portal',
    description: 'User management, RBAC matrix, and system parameters',
    requiredPermission: 'USER_MANAGE',
  },
};

export const NAVIGATION_ITEMS: NavigationItemConfig[] = [
  // MAIN PORTAL
  {
    id: 'nav-dashboard',
    label: 'Dashboard',
    path: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    group: 'WORKSPACE',
    portal: 'MAIN',
  },
  {
    id: 'nav-my-requests',
    label: 'My Requisitions',
    path: ROUTES.REQUESTS.LIST,
    icon: FileText,
    group: 'WORKSPACE',
    portal: 'MAIN',
    requiredPermissions: ['REQUEST_VIEW'],
  },
  {
    id: 'nav-approvals',
    label: 'Approvals Queue',
    path: ROUTES.APPROVALS.LIST,
    icon: CheckSquare,
    group: 'WORKSPACE',
    portal: 'MAIN',
    requiredPermissions: ['REQUEST_APPROVE', 'NFA_APPROVE', 'APPROVAL_VIEW'],
  },
  {
    id: 'nav-pipeline',
    label: 'Procurement Pipeline',
    path: ROUTES.PIPELINE.LIST,
    icon: Activity,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['REQUEST_VIEW'],
  },
  {
    id: 'nav-pr',
    label: 'PR Control Center',
    path: ROUTES.PR.LIST,
    icon: FileText,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['PR_VIEW'],
  },
  {
    id: 'nav-nfa',
    label: 'Notes For Approval',
    path: ROUTES.NFA.LIST,
    icon: FileCheck,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['NFA_VIEW'],
  },
  {
    id: 'nav-lfa',
    label: 'Letters For Award',
    path: ROUTES.LFA.LIST,
    icon: Award,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['LFA_VIEW'],
  },
  {
    id: 'nav-rfq',
    label: 'RFQ Sourcing',
    path: ROUTES.RFQ.LIST,
    icon: Send,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['RFQ_VIEW'],
  },
  {
    id: 'nav-vendors',
    label: 'Vendor Directory',
    path: ROUTES.VENDORS.LIST,
    icon: Building2,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['VENDOR_VIEW'],
  },
  {
    id: 'nav-quotations',
    label: 'Quotation Matrix',
    path: ROUTES.QUOTATIONS.LIST,
    icon: FileSpreadsheet,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['QUOTATION_VIEW'],
  },
  {
    id: 'nav-po',
    label: 'Purchase Orders',
    path: ROUTES.PURCHASE_ORDERS.LIST,
    icon: ShoppingBag,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['PO_VIEW'],
  },
  {
    id: 'nav-deliveries',
    label: 'Delivery Tracking',
    path: ROUTES.DELIVERIES.TRACKING,
    icon: Truck,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['GRN_VIEW'],
  },
  {
    id: 'nav-grn',
    label: 'Goods Receipts',
    path: ROUTES.GRN.LIST,
    icon: PackageCheck,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['GRN_VIEW'],
  },
  {
    id: 'nav-invoices',
    label: 'Invoices (3-Way)',
    path: ROUTES.INVOICES.LIST,
    icon: Receipt,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['INVOICE_VIEW'],
  },
  {
    id: 'nav-payments',
    label: 'Payments',
    path: ROUTES.PAYMENTS.LIST,
    icon: CreditCard,
    group: 'PROCUREMENT',
    portal: 'MAIN',
    requiredPermissions: ['PAYMENT_VIEW'],
  },
  {
    id: 'nav-analytics',
    label: 'Analytics & Spend',
    path: ROUTES.ANALYTICS,
    icon: BarChart3,
    group: 'INTELLIGENCE',
    portal: 'MAIN',
    requiredPermissions: ['ANALYTICS_VIEW'],
  },
  {
    id: 'nav-ai',
    label: 'ProcureAI Intelligence',
    path: ROUTES.AI,
    icon: Bot,
    group: 'INTELLIGENCE',
    portal: 'MAIN',
    requiredPermissions: ['AI_RECOMMENDATIONS'],
    badge: 'AI',
  },
  {
    id: 'nav-audit',
    label: 'Audit Trail',
    path: ROUTES.AUDIT_LOGS,
    icon: ShieldCheck,
    group: 'GOVERNANCE',
    portal: 'MAIN',
    requiredPermissions: ['AUDIT_VIEW'],
  },
  {
    id: 'nav-integration-logs',
    label: 'Integration Logs',
    path: ROUTES.INTEGRATION_LOGS,
    icon: Activity,
    group: 'GOVERNANCE',
    portal: 'MAIN',
    requiredPermissions: ['INTEGRATION_LOGS_VIEW'],
  },
  {
    id: 'nav-main-user-management',
    label: 'User Management',
    path: ROUTES.ADMIN.USERS,
    icon: Users,
    group: 'ADMINISTRATION',
    portal: 'MAIN',
    requiredPermissions: ['USER_MANAGE'],
  },
  {
    id: 'nav-main-roles',
    label: 'Roles & RBAC Matrix',
    path: ROUTES.ADMIN.ROLES,
    icon: ShieldCheck,
    group: 'ADMINISTRATION',
    portal: 'MAIN',
    requiredPermissions: ['ROLE_MANAGE'],
  },
  {
    id: 'nav-main-approval-matrix',
    label: 'Approval Threshold Rules',
    path: ROUTES.ADMIN.APPROVAL_MATRIX,
    icon: Sliders,
    group: 'ADMINISTRATION',
    portal: 'MAIN',
    requiredPermissions: ['USER_MANAGE'],
  },
  {
    id: 'nav-main-config',
    label: 'System Configuration',
    path: ROUTES.ADMIN.CONFIGURATION,
    icon: SlidersHorizontal,
    group: 'ADMINISTRATION',
    portal: 'MAIN',
    requiredPermissions: ['USER_MANAGE'],
  },

  // INTERNAL PROCUREMENT PORTAL
  {
    id: 'nav-internal-dashboard',
    label: 'Requisitioner Home',
    path: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    group: 'WORKSPACE',
    portal: 'INTERNAL_PROCUREMENT',
  },
  {
    id: 'nav-internal-requests',
    label: 'My Requisitions',
    path: ROUTES.REQUESTS.LIST,
    icon: FileText,
    group: 'WORKSPACE',
    portal: 'INTERNAL_PROCUREMENT',
    requiredPermissions: ['REQUEST_VIEW'],
  },
  {
    id: 'nav-internal-new-req',
    label: 'Create Requisition',
    path: ROUTES.REQUESTS.NEW,
    icon: FileText,
    group: 'WORKSPACE',
    portal: 'INTERNAL_PROCUREMENT',
    requiredPermissions: ['REQUEST_CREATE'],
  },
  {
    id: 'nav-internal-approvals',
    label: 'My Approvals',
    path: ROUTES.APPROVALS.LIST,
    icon: CheckSquare,
    group: 'WORKSPACE',
    portal: 'INTERNAL_PROCUREMENT',
    requiredPermissions: ['REQUEST_APPROVE'],
  },

  // SAP SIMULATOR PORTAL
  {
    id: 'nav-sap-dashboard',
    label: 'SAP Simulator Dashboard',
    path: ROUTES.SAP.DASHBOARD,
    icon: LayoutDashboard,
    group: 'SAP ERP OVERVIEW',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-materials',
    label: 'SAP Material Master',
    path: ROUTES.SAP.MATERIALS,
    icon: Database,
    group: 'SAP ERP MASTER',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-vendors',
    label: 'SAP Vendor Master',
    path: ROUTES.SAP.VENDORS,
    icon: Database,
    group: 'SAP ERP MASTER',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-masters',
    label: 'Org & Budget Masters',
    path: ROUTES.SAP.MASTERS,
    icon: Layers,
    group: 'SAP ERP MASTER',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-pr',
    label: 'SAP Requisitions (ME51N)',
    path: ROUTES.SAP.PR,
    icon: Layers,
    group: 'SAP TRANSACTIONS',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-po',
    label: 'SAP Orders (ME21N)',
    path: ROUTES.SAP.PO,
    icon: ShoppingBag,
    group: 'SAP TRANSACTIONS',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-grn',
    label: 'SAP Receipts (MIGO)',
    path: ROUTES.SAP.GRN,
    icon: PackageCheck,
    group: 'SAP TRANSACTIONS',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-invoices',
    label: 'SAP Invoices (MIRO)',
    path: ROUTES.SAP.INVOICES,
    icon: Receipt,
    group: 'SAP TRANSACTIONS',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-payments',
    label: 'SAP Payments (F110)',
    path: ROUTES.SAP.PAYMENTS,
    icon: CreditCard,
    group: 'SAP TRANSACTIONS',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-logs',
    label: 'Integration Audit Logs',
    path: ROUTES.SAP.LOGS,
    icon: Activity,
    group: 'INTEGRATION',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },
  {
    id: 'nav-sap-control',
    label: 'Simulation Control',
    path: ROUTES.SAP.CONTROL,
    icon: ShieldCheck,
    group: 'INTEGRATION',
    portal: 'SAP_SIMULATOR',
    isSap: true,
  },

  // ADMIN PORTAL
  {
    id: 'nav-admin-users',
    label: 'User Management',
    path: ROUTES.ADMIN.USERS,
    icon: Users,
    group: 'ADMINISTRATION',
    portal: 'ADMIN',
    requiredPermissions: ['USER_MANAGE'],
  },
  {
    id: 'nav-admin-roles',
    label: 'Roles & RBAC Matrix',
    path: ROUTES.ADMIN.ROLES,
    icon: ShieldCheck,
    group: 'ADMINISTRATION',
    portal: 'ADMIN',
    requiredPermissions: ['ROLE_MANAGE'],
  },
  {
    id: 'nav-admin-approval-matrix',
    label: 'Approval Threshold Rules',
    path: ROUTES.ADMIN.APPROVAL_MATRIX,
    icon: Sliders,
    group: 'ADMINISTRATION',
    portal: 'ADMIN',
    requiredPermissions: ['USER_MANAGE'],
  },
  {
    id: 'nav-admin-config',
    label: 'System Configuration',
    path: ROUTES.ADMIN.CONFIGURATION,
    icon: SlidersHorizontal,
    group: 'ADMINISTRATION',
    portal: 'ADMIN',
    requiredPermissions: ['USER_MANAGE'],
  },
];

export function getNavigationForPortal(portal: PortalContext): NavigationItemConfig[] {
  return NAVIGATION_ITEMS.filter((item) => item.portal === portal);
}

export function getAvailablePortalsForUser(user: User | null): PortalContext[] {
  if (!user || user.status !== 'ACTIVE') return [];
  if (user.role === 'ADMIN') return ['MAIN', 'INTERNAL_PROCUREMENT', 'ADMIN', 'SAP_SIMULATOR'];
  return ROLE_PORTALS_MAP[user.role] || ['MAIN'];
}
